import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import type { Tone } from '../format';

/* ------------------------------------------------------------------ icons */
const paths: Record<string, string> = {
  today: 'M4 6h16M4 12h10M4 18h16M18 9l3 3-3 3',
  bookings: 'M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
  plus: 'M12 5v14M5 12h14',
  rooms: 'M3 19V9l9-5 9 5v10M3 19h18M9 19v-5h6v5',
  moon: 'M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z',
  out: 'M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4M16 8l4 4-4 4M20 12H9',
  inbox: 'M3 13l3-8h12l3 8M3 13v6h18v-6M3 13h5l1 2h6l1-2h5',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-4.3-4.3',
  check: 'M5 12l5 5 9-10',
  back: 'M15 5l-7 7 7 7',
  people: 'M16 19v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 19v-1a3.5 3.5 0 0 0-2.5-3.3M15.5 4.2a3.5 3.5 0 0 1 0 6.6',
  shield: 'M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3zM9 12l2 2 4-4',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  books: 'M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4zM5 18a2 2 0 0 1 2-2h11M9 8h6M9 12h6',
  broom: 'M14 4l6 6M12.5 5.5l6 6-7 7H5.5l-1-1v-5l8-7zM7 13l4 4M9 11l4 4',
  utensils: 'M7 3v8M4 3v5a3 3 0 0 0 6 0V3M7 11v10M17 3c-2 1.5-3 4-3 7 0 2 1 3 3 3v8',
  flame: 'M12 3c1 3 5 5 5 10a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-5 1-9z',
  till: 'M5 9h14l1 4H4l1-4zM6 13v6h12v-6M9 6h6v3H9zM10 16h4',
  book: 'M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4zM9 8h6M9 12h4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  wave: 'M3 15c2 0 2-2 4.5-2S10 15 12 15s2.500-2 4.500-2S19 15 21 15M3 19c2 0 2-2 4.500-2S10 19 12 19s2.500-2 4.500-2S19 19 21 19M8 10V5a2 2 0 0 1 4 0M13 10V6a2 2 0 0 1 4 0',
  car: 'M5 16V11l2-5h10l2 5v5M3 16h18M7 19v-3M17 19v-3M7 11h10',
  wallet: 'M4 7a2 2 0 0 1 2-2h12v3M4 7v11a2 2 0 0 0 2 2h14V8H6a2 2 0 0 1-2-2M16 14h.01',
  setup: 'M4 6h10M18 6h2M4 12h2M10 12h10M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4',
};

export function Icon({ name }: { name: keyof typeof paths | string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] ?? ''} />
    </svg>
  );
}

/* ----------------------------------------------------------------- button */
interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'danger' | 'ghost' | '';
  size?: 'small' | '';
  block?: boolean;
  loading?: boolean;
}
export function Button({ variant = '', size = '', block, loading, children, disabled, className = '', ...rest }: BtnProps) {
  const cls = ['btn', variant, size, block ? 'block' : '', className].filter(Boolean).join(' ');
  return (
    <button className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? 'Working…' : children}
    </button>
  );
}

export const Badge = ({ tone = '', children }: { tone?: Tone; children: ReactNode }) => <span className={`badge ${tone}`}>{children}</span>;

/* ------------------------------------------------------------------ fields */
export function Field({ label, hint, error, children, id }: { label: string; hint?: string; error?: string | undefined; children: ReactNode; id: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? <span className="err" role="alert">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ states */
export function Skeleton({ h = 18, w = '100%' }: { h?: number; w?: string }) {
  return <div className="skeleton" style={{ height: h, width: w }} aria-hidden="true" />;
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} h={64} />
      ))}
    </div>
  );
}

export function Empty({ icon = 'inbox', title, children }: { icon?: string; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <Icon name={icon} />
      <strong>{title}</strong>
      {children ? <span className="small">{children}</span> : null}
    </div>
  );
}

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : 'Something went wrong. Please try again.';
  return (
    <div className="banner bad" role="alert">
      <div className="row">
        <span>{msg}</span>
        {retry ? (
          <Button size="small" onClick={retry}>
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- modal */
export function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const wanted = useRef(open);
  wanted.current = open;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  // The browser also reports a close when the app itself hides a dialog (to show another in its place). Only a close the
  // person asked for (Escape, the backdrop, a Close button) may tell the page to forget what it was showing.
  const closed = () => {
    if (wanted.current) onClose();
  };
  return (
    <dialog ref={ref} className="modal" onClose={closed} onClick={(e) => e.target === ref.current && onClose()} aria-label={title}>
      {open ? (
        <div className="modal-body">
          <h2 className="h2">{title}</h2>
          {children}
        </div>
      ) : null}
    </dialog>
  );
}

/* ------------------------------------------------------------------ toasts */
interface ToastApi {
  ok: (m: string) => void;
  fail: (m: string) => void;
}
const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<{ id: number; msg: string; bad: boolean }[]>([]);
  const next = useRef(1);
  const push = useCallback((msg: string, bad: boolean) => {
    const id = next.current++;
    setItems((x) => [...x, { id, msg, bad }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), bad ? 6000 : 3500);
  }, []);
  const api = useMemo<ToastApi>(() => ({ ok: (m) => push(m, false), fail: (m) => push(m, true) }), [push]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.bad ? 'bad' : ''}`} role={t.bad ? 'alert' : 'status'}>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast must be used inside ToastProvider');
  return v;
}

/** Turns any thrown error into a toast with a safe message. */
export function useReport(): (e: unknown) => void {
  const t = useToast();
  return useCallback((e) => t.fail(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.'), [t]);
}
